import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import * as FileSaver from 'file-saver'
import * as XLS from 'xlsx'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import SearchIcon from '@mui/icons-material/Search'
import API from '../../component/Axios'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import Pagination from '../../component/Pagination'
import ResetFilterButton from '../../component/ResetFilterButton'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import Title from '../../component/Title'
import { LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_SEARCH_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import { formatDate, formatDateTimeText } from '../../utils/dateDisplay'
import { createInitialListState, buildListSearch as buildListSearchQuery } from '../../utils/listPageState'

const CAMPAIGN_CODE = 'SEOUL25_POINT'
const PACKAGE_CODE = 'SEOUL25'
const COMMON_CHANNEL = 'COMMON'
const DEFAULT_EXPIRES_AT = '2026-12-31 23:59:59'
const MAX_GENERATION_QUANTITY = 20000
const DEFAULT_CODE_PREFIX = 'SE25'
const CODE_PREFIX_REGEX = /^[A-Z0-9]{2,20}$/
const INPUT_CODE_REGEX = /^[A-Z0-9]{4,30}$/
const POINT_AMOUNT_OPTIONS = [
    { value: 5000, label: '5,000P', quantityField: 'point5000Quantity', color: 'success' },
    { value: 3000, label: '3,000P', quantityField: 'point3000Quantity', color: 'warning' },
    { value: 2000, label: '2,000P', quantityField: 'point2000Quantity', color: 'info' },
    { value: 1000, label: '1,000P', quantityField: 'point1000Quantity', color: 'default' },
]
const ALLOWED_POINT_AMOUNTS = POINT_AMOUNT_OPTIONS.map((option) => option.value)
const GENERATION_TYPE = {
    RANDOM: 'random',
    MANUAL: 'manual',
}
const USAGE_TYPE = {
    SINGLE: 'single',
    COMMON: 'common',
}

const DEFAULT_SEARCH_SELECT = {
    campaignCode: CAMPAIGN_CODE,
    packageCode: PACKAGE_CODE,
    usageType: '',
    pointAmount: '',
    status: '',
    batchNo: '',
}

const SEARCH_SELECT_PARAMS = {
    campaignCode: { param: 'campaignCode', defaultValue: DEFAULT_SEARCH_SELECT.campaignCode },
    packageCode: { param: 'packageCode', defaultValue: DEFAULT_SEARCH_SELECT.packageCode },
    usageType: { param: 'usageType', defaultValue: DEFAULT_SEARCH_SELECT.usageType },
    pointAmount: { param: 'pointAmount', defaultValue: DEFAULT_SEARCH_SELECT.pointAmount },
    status: { param: 'status', defaultValue: DEFAULT_SEARCH_SELECT.status },
    batchNo: { param: 'batchNo', defaultValue: DEFAULT_SEARCH_SELECT.batchNo },
}

const DEFAULT_GENERATION_FORM = {
    generationType: GENERATION_TYPE.RANDOM,
    usageType: USAGE_TYPE.SINGLE,
    campaignCode: CAMPAIGN_CODE,
    packageCode: PACKAGE_CODE,
    codePrefix: DEFAULT_CODE_PREFIX,
    code: '',
    pointAmount: '5000',
    point5000Quantity: '3125',
    point3000Quantity: '3125',
    point2000Quantity: '3125',
    point1000Quantity: '3125',
    batchNo: '',
    expiresAt: DEFAULT_EXPIRES_AT,
}

const CODE_TABLE_COLUMNS = [
    { width: 64 },
    { width: 160 },
    { width: 96 },
    { width: 96 },
    { width: 100 },
    { width: 90 },
    { width: 110 },
    { width: 220 },
    { width: 170 },
    { width: 160 },
    { width: 160 },
]

const CODE_TABLE_SX = {
    minWidth: CODE_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0),
    tableLayout: 'fixed',
    '& th': {
        whiteSpace: 'nowrap',
    },
    '& td': {
        verticalAlign: 'middle',
    },
}

const NOWRAP_CELL_SX = {
    whiteSpace: 'nowrap',
}

const BATCH_NO_CELL_SX = {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-all',
    lineHeight: 1.35,
}

const CODE_TEXT_SX = {
    fontFamily: 'monospace',
    fontWeight: 700,
    whiteSpace: 'nowrap',
}

const generationPanelSx = {
    mb: 2,
    p: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    backgroundColor: 'background.default',
}

const generationFormSx = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1,
}

const resultCodeListSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    mt: 1,
}

const getInitialListState = (location) => createInitialListState({
    location,
    defaultSearchSelect: DEFAULT_SEARCH_SELECT,
    selectParams: SEARCH_SELECT_PARAMS,
})

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => buildListSearchQuery({
    curPage,
    searchQuery,
    searchSelect,
    selectParams: SEARCH_SELECT_PARAMS,
})

const getTotalCount = (count) => Number(count || 0)

const normalizeQuantity = (value) => {
    if (value === undefined || value === null || value === '') {
        return 0
    }

    const parsedValue = Number(value)

    return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : 0
}

const normalizeCodeInput = (value) => String(value || '').trim().toUpperCase().replace(/\s+/g, '').replace(/-/g, '')

const getUsageTypeLabel = (usageType) => {
    if (usageType === USAGE_TYPE.COMMON) {
        return '공용'
    }

    if (usageType === USAGE_TYPE.SINGLE) {
        return '1회용'
    }

    return usageType || '-'
}

const getUsageTypeChip = (usageType) => {
    if (usageType === USAGE_TYPE.COMMON) {
        return <StatusChip label="공용" color="secondary" variant="outlined" />
    }

    if (usageType === USAGE_TYPE.SINGLE) {
        return <StatusChip label="1회용" color="default" variant="outlined" />
    }

    return <StatusChip label={usageType || '-'} variant="outlined" />
}

const getStatusChip = (status) => {
    if (status === 'generated') {
        return <StatusChip label="미사용" color="info" />
    }

    if (status === 'used') {
        return <StatusChip label="사용" color="success" />
    }

    if (status === 'void') {
        return <StatusChip label="폐기" color="default" variant="outlined" />
    }

    return <StatusChip label={status || '-'} variant="outlined" />
}

const getPointChip = (pointAmount) => {
    const option = POINT_AMOUNT_OPTIONS.find(({ value }) => value === Number(pointAmount))

    if (option) {
        return <StatusChip label={option.label} color={option.color} variant="outlined" />
    }

    return <StatusChip label={pointAmount ? `${pointAmount}P` : '-'} variant="outlined" />
}

const buildExcelDownloadRows = (rows = []) => (
    rows.map((row, index) => ({
        No: index + 1,
        포인트쿠폰코드: row.포인트쿠폰코드 || row.code || '',
        포인트: row.포인트 || row.pointAmount || '',
    }))
)

export default function PointCouponCodes() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [generationLoading, setGenerationLoading] = useState(false)
    const [excelLoading, setExcelLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [generationForm, setGenerationForm] = useState({ ...DEFAULT_GENERATION_FORM })
    const [generationResult, setGenerationResult] = useState(null)
    const offset = 10
    const isBusy = actionLoading || generationLoading || excelLoading

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setActionLoading(true)
        setListErrorMessage('')

        try {
            const { data: response } = await API.post('/api/v1/admin/pointCoupon/getCodeList', {
                page: curPage,
                offset,
                searchText: searchQuery,
                searchSelect,
            })

            if ([1, 200].includes(Number(response.code))) {
                setTotalCount(getTotalCount(response.data?.count))
                setData(response.data?.rows || [])
                setListErrorMessage('')
            } else if (response.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = response.message || '포인트 쿠폰 코드 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '포인트 쿠폰 코드 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
            setActionLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const showSnackbar = (severity, message) => {
        setOpenSnackbar(true)
        setSeveritySnackbar(severity)
        setMessageSnackbar(message)
    }

    const updateSearchSelect = (e) => {
        const { name, value } = e.target

        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect((prevSearchSelect) => ({
            ...prevSearchSelect,
            [name]: value,
        }))
    }

    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage !== 1) {
            setCurPage(1)
        }

        setSearchQuery(trimmedSearchText)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({ ...DEFAULT_SEARCH_SELECT })
    }

    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? defaultValue

            return String(currentValue) !== String(defaultValue)
        })
    }

    const updateGenerationForm = (e) => {
        const { name, value } = e.target

        if (generationLoading) {
            return
        }

        setGenerationForm((prevGenerationForm) => ({
            ...prevGenerationForm,
            ...(name === 'generationType' && value === GENERATION_TYPE.RANDOM ? { usageType: USAGE_TYPE.SINGLE } : {}),
            [name]: value,
        }))
    }

    const buildGenerationPayload = () => {
        const generationType = generationForm.generationType || GENERATION_TYPE.RANDOM
        const isManual = generationType === GENERATION_TYPE.MANUAL
        const basePayload = {
            generationType,
            usageType: isManual ? generationForm.usageType : USAGE_TYPE.SINGLE,
            campaignCode: (generationForm.campaignCode || '').trim().toUpperCase(),
            packageCode: (generationForm.packageCode || '').trim().toUpperCase(),
            channel: COMMON_CHANNEL,
            expiresAt: (generationForm.expiresAt || '').trim(),
            batchNo: (generationForm.batchNo || '').trim(),
        }

        if (isManual) {
            return {
                ...basePayload,
                code: normalizeCodeInput(generationForm.code),
                pointAmount: Number(generationForm.pointAmount || 0),
                totalQuantity: 1,
            }
        }

        const amounts = POINT_AMOUNT_OPTIONS.map(({ value, quantityField }) => ({
            pointAmount: value,
            quantity: normalizeQuantity(generationForm[quantityField]),
        })).filter((item) => item.quantity > 0)

        return {
            ...basePayload,
            codePrefix: normalizeCodeInput(generationForm.codePrefix || DEFAULT_CODE_PREFIX),
            amounts,
            totalQuantity: amounts.reduce((sum, item) => sum + item.quantity, 0),
        }
    }

    const validateGenerationPayload = (payload) => {
        if (!payload.campaignCode) {
            return '캠페인 코드를 입력해주세요.'
        }

        if (!payload.packageCode) {
            return '패키지 코드를 입력해주세요.'
        }

        if (!payload.expiresAt) {
            return '등록 가능 기한을 입력해주세요.'
        }

        if (payload.generationType === GENERATION_TYPE.MANUAL) {
            if (!INPUT_CODE_REGEX.test(payload.code || '')) {
                return '포인트 쿠폰 코드는 영문 대문자와 숫자 4~30자로 입력해주세요.'
            }

            if (!ALLOWED_POINT_AMOUNTS.includes(Number(payload.pointAmount))) {
                return '포인트 금액은 1,000P, 2,000P, 3,000P 또는 5,000P만 입력할 수 있습니다.'
            }

            return ''
        }

        if (!CODE_PREFIX_REGEX.test(payload.codePrefix || '')) {
            return '코드 Prefix는 영문 대문자와 숫자 2~20자로 입력해주세요.'
        }

        if (payload.totalQuantity < 1 || payload.totalQuantity > MAX_GENERATION_QUANTITY) {
            return `코드 수량은 1~${MAX_GENERATION_QUANTITY}개로 입력해주세요.`
        }

        return ''
    }

    const requestCodeGeneration = async (type) => {
        if (generationLoading) {
            return
        }

        const payload = buildGenerationPayload()
        const validationMessage = validateGenerationPayload(payload)

        if (validationMessage) {
            showSnackbar('error', validationMessage)
            return
        }

        if (type === 'generate') {
            const amountText = payload.generationType === GENERATION_TYPE.MANUAL
                ? `${payload.code} / ${Number(payload.pointAmount).toLocaleString()}P / ${getUsageTypeLabel(payload.usageType)}`
                : payload.amounts.map((item) => `${item.pointAmount.toLocaleString()}P ${item.quantity.toLocaleString()}개`).join(', ')
            const confirmed = window.confirm(`${payload.packageCode} 포인트 쿠폰 ${payload.totalQuantity.toLocaleString()}개를 생성합니다.\n${amountText}\n채널은 ${COMMON_CHANNEL}로 저장됩니다. 진행할까요?`)

            if (!confirmed) {
                return
            }
        }

        try {
            setGenerationLoading(true)
            const url = type === 'preview'
                ? '/api/v1/admin/pointCoupon/previewCodeGeneration'
                : '/api/v1/admin/pointCoupon/generateCodes'
            const { data: response } = await API.post(url, payload)

            if ([1, 200].includes(Number(response.code))) {
                setGenerationResult(response.data)
                showSnackbar('success', type === 'preview' ? '포인트 쿠폰 생성 미리보기를 완료했습니다.' : '포인트 쿠폰 코드를 생성했습니다.')

                if (type === 'generate') {
                    getData(false)
                }
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                showSnackbar('error', response.message || '포인트 쿠폰 생성 요청에 실패했습니다.')
            }
        } catch (_error) {
            showSnackbar('error', '포인트 쿠폰 생성 요청에 실패했습니다.')
        } finally {
            setGenerationLoading(false)
        }
    }

    const exportToExcel = async () => {
        if (excelLoading) {
            return
        }

        try {
            setExcelLoading(true)
            const { data: response } = await API.post('/api/v1/admin/pointCoupon/getExcelCodeList', {
                searchText: searchQuery,
                searchSelect,
            })

            if ([1, 200].includes(Number(response.code))) {
                const rows = response.data || []

                if (rows.length === 0) {
                    showSnackbar('error', '엑셀 데이터가 없습니다.')
                    return
                }

                const ws = XLS.utils.json_to_sheet(buildExcelDownloadRows(rows), {
                    header: ['No', '포인트쿠폰코드', '포인트'],
                })
                const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
                const excelBuffer = XLS.write(wb, { bookType: 'xlsx', type: 'array' })
                const excelData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' })
                FileSaver.saveAs(excelData, `point-coupon-codes-${formatDate(new Date())}.xlsx`)
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                showSnackbar('error', response.message || '엑셀 다운로드에 실패했습니다.')
            }
        } catch (_error) {
            showSnackbar('error', '엑셀 다운로드에 실패했습니다.')
        } finally {
            setExcelLoading(false)
        }
    }

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="포인트 쿠폰 코드 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={isBusy}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 포인트 쿠폰 코드가 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 포인트 쿠폰 코드가 없습니다."
                description="포인트 금액별 수량을 입력해 코드를 생성하면 이 목록에 표시됩니다."
            />
        )
    }

    const renderGenerationResult = () => {
        if (!generationResult) {
            return null
        }

        const codes = generationResult.codes || generationResult.sampleCodes || []
        const existingCount = generationResult.existingCount || generationResult.existingCountAfter
        const amountText = (generationResult.amounts || [])
            .map((item) => `${Number(item.pointAmount).toLocaleString()}P ${Number(item.quantity).toLocaleString()}개`)
            .join(', ')

        return (
            <Alert severity={generationResult.previewOnly ? 'info' : 'success'} sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {generationResult.previewOnly ? '미리보기' : '생성 완료'}: {generationResult.packageCode} / {generationResult.channel} / {getUsageTypeLabel(generationResult.usageType)} / 총 {Number(generationResult.totalQuantity || 0).toLocaleString()}개
                </Typography>
                {amountText && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {amountText}
                    </Typography>
                )}
                {existingCount && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        기존 코드: 총 {existingCount.total || 0}개, 미사용 {existingCount.generated || 0}개, 사용 {existingCount.used || 0}개, 폐기 {existingCount.void || 0}개
                    </Typography>
                )}
                {codes.length > 0 && (
                    <Box sx={resultCodeListSx}>
                        {codes.slice(0, 20).map((item) => (
                            <Box key={item.code} component="span" sx={CODE_TEXT_SX}>{item.code}</Box>
                        ))}
                        {codes.length > 20 && <Box component="span">외 {codes.length - 20}개</Box>}
                    </Box>
                )}
            </Alert>
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner />
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose} />}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>패키지 포인트 코드 관리</Title>

                    <Box sx={generationPanelSx}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            패키지 포인트 코드 생성
                        </Typography>
                        <Box sx={generationFormSx}>
                            <TextField
                                label="생성 방식"
                                select
                                name="generationType"
                                value={generationForm.generationType}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                sx={{ width: 130 }}
                            >
                                <MenuItem value={GENERATION_TYPE.RANDOM}>랜덤 대량</MenuItem>
                                <MenuItem value={GENERATION_TYPE.MANUAL}>수동 단일</MenuItem>
                            </TextField>
                            <TextField
                                label="캠페인 코드"
                                name="campaignCode"
                                value={generationForm.campaignCode}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                sx={{ width: 170 }}
                            />
                            <TextField
                                label="패키지 코드"
                                name="packageCode"
                                value={generationForm.packageCode}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                sx={{ width: 140 }}
                            />
                            {generationForm.generationType === GENERATION_TYPE.MANUAL ? (
                                <>
                                    <TextField
                                        label="사용 방식"
                                        select
                                        name="usageType"
                                        value={generationForm.usageType}
                                        onChange={updateGenerationForm}
                                        size="small"
                                        disabled={generationLoading}
                                        sx={{ width: 120 }}
                                    >
                                        <MenuItem value={USAGE_TYPE.SINGLE}>1회용</MenuItem>
                                        <MenuItem value={USAGE_TYPE.COMMON}>공용</MenuItem>
                                    </TextField>
                                    <TextField
                                        label="쿠폰 코드"
                                        name="code"
                                        value={generationForm.code}
                                        onChange={updateGenerationForm}
                                        size="small"
                                        disabled={generationLoading}
                                        sx={{ width: 170 }}
                                    />
                                    <TextField
                                        label="포인트"
                                        select
                                        name="pointAmount"
                                        value={generationForm.pointAmount}
                                        onChange={updateGenerationForm}
                                        size="small"
                                        disabled={generationLoading}
                                        sx={{ width: 120 }}
                                    >
                                        {POINT_AMOUNT_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={String(option.value)}>{option.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </>
                            ) : (
                                <>
                                    <TextField
                                        label="코드 Prefix"
                                        name="codePrefix"
                                        value={generationForm.codePrefix}
                                        onChange={updateGenerationForm}
                                        size="small"
                                        disabled={generationLoading}
                                        sx={{ width: 120 }}
                                    />
                                    {POINT_AMOUNT_OPTIONS.map((option) => (
                                        <TextField
                                            key={option.value}
                                            label={`${option.label} 수량`}
                                            name={option.quantityField}
                                            type="number"
                                            value={generationForm[option.quantityField]}
                                            onChange={updateGenerationForm}
                                            size="small"
                                            disabled={generationLoading}
                                            inputProps={{ min: 0, max: MAX_GENERATION_QUANTITY }}
                                            sx={{ width: 130 }}
                                        />
                                    ))}
                                </>
                            )}
                            <TextField
                                label="배치번호"
                                name="batchNo"
                                value={generationForm.batchNo}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                sx={{ width: 180 }}
                            />
                            <TextField
                                label="등록 가능 기한"
                                name="expiresAt"
                                value={generationForm.expiresAt}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                sx={{ width: 190 }}
                            />
                            <TextField
                                label="채널"
                                value={COMMON_CHANNEL}
                                size="small"
                                disabled
                                sx={{ width: 120 }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={generationLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => requestCodeGeneration('preview')}
                            >
                                미리보기
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<Add />}
                                disabled={generationLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => requestCodeGeneration('generate')}
                            >
                                생성
                            </Button>
                        </Box>
                        {renderGenerationResult()}
                    </Box>

                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="포인트 쿠폰 코드"
                                type="search"
                                name="searchText"
                                value={searchText}
                                disabled={isBusy}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight: 2 }} orientation="vertical" />
                            <TextField
                                label="캠페인 코드"
                                name="campaignCode"
                                value={searchSelect.campaignCode}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ width: 170 }}
                            />
                            <TextField
                                label="패키지 코드"
                                name="packageCode"
                                value={searchSelect.packageCode}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ width: 140 }}
                            />
                            <TextField
                                label="포인트"
                                select
                                name="pointAmount"
                                value={searchSelect.pointAmount}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                {POINT_AMOUNT_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={String(option.value)}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="사용 방식"
                                select
                                name="usageType"
                                value={searchSelect.usageType}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value={USAGE_TYPE.SINGLE}>1회용</MenuItem>
                                <MenuItem value={USAGE_TYPE.COMMON}>공용</MenuItem>
                            </TextField>
                            <TextField
                                label="상태"
                                select
                                name="status"
                                value={searchSelect.status}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ minWidth: 130 }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="generated">미사용</MenuItem>
                                <MenuItem value="used">사용</MenuItem>
                                <MenuItem value="void">폐기</MenuItem>
                            </TextField>
                            <TextField
                                label="배치번호"
                                name="batchNo"
                                value={searchSelect.batchNo}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ width: 180 }}
                            />
                        </ListFilterForm>
                    </ListFilterBar>

                    <ListTableToolbar
                        left={(
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                disabled={isBusy}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={exportToExcel}
                            >
                                엑셀
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                        <Table size="small" sx={CODE_TABLE_SX}>
                            <colgroup>
                                {CODE_TABLE_COLUMNS.map((column, index) => (
                                    <col key={`point-coupon-code-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>코드</TableCell>
                                    <TableCell>포인트</TableCell>
                                    <TableCell>상태</TableCell>
                                    <TableCell>사용 방식</TableCell>
                                    <TableCell>사용 수</TableCell>
                                    <TableCell>사용 유저</TableCell>
                                    <TableCell>배치번호</TableCell>
                                    <TableCell>만료일</TableCell>
                                    <TableCell>생성일</TableCell>
                                    <TableCell>사용일</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? data.map((row, index) => (
                                    <TableRow key={row.pointCouponCodeId}>
                                        <TableCell sx={NOWRAP_CELL_SX}>{(curPage - 1) * offset + index + 1}</TableCell>
                                        <TableCell sx={CODE_TEXT_SX}>{row.code || '-'}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{getPointChip(row.pointAmount)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{getStatusChip(row.status)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{getUsageTypeChip(row.usageType)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{Number(row.usedCount || 0).toLocaleString()}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{row.usedUserId || '-'}</TableCell>
                                        <TableCell sx={BATCH_NO_CELL_SX}>{row.batchNo || '-'}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{formatDateTimeText(row.expiresAt)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{formatDateTimeText(row.regTime)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{formatDateTimeText(row.usedAt)}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={11}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose} />}
                </Paper>
            )}
        </Grid>
    )
}
